import { Box, Button, TextField, Typography } from '@mui/material';
import { FieldErrors, FieldsAPIError } from 'errors';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useState } from 'react';

import ApiContext from 'contexts/apiContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorContext from 'contexts/errorContext';
import { makeStyles } from 'tss-react/mui';
import { sendPasswordResetEmail } from 'firebase/auth';

const useStyles = makeStyles()((theme) => ({
  form: {
    marginTop: theme.spacing(1),
    width: '100%',
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  box: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(1),
  },
  link: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
}));

export default function ResetPasswordScreen() {
  const { auth } = useContext(ApiContext);
  const { setError } = useContext(ErrorContext);
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const isWelcome = query.get('context') === 'welcome';
  const prefilledEmail = query.get('email') || '';
  const [email, setEmail] = useState(prefilledEmail);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [success, setSuccess] = useState(false);

  const getLoginRoute = (emailValue: string) => {
    const trimmedEmail = emailValue.trim();
    if (!trimmedEmail) return '/auth/login';
    return `/auth/login?email=${encodeURIComponent(trimmedEmail)}`;
  };

  const handleChange = (event: any) => {
    setEmail(event.target.value);
  };

  const submit = (event: any) => {
    event.preventDefault();
    const trimmedEmail = email.trim();
    const actionCodeSettings = {
      url: `${window.location.origin}/auth/login?email=${encodeURIComponent(trimmedEmail)}`,
      handleCodeInApp: false,
    };
    sendPasswordResetEmail(auth, trimmedEmail, actionCodeSettings)
      .then(() => setSuccess(true))
      .catch((err) => {
        if (err.code === 'auth/invalid-email') {
          setError({ message: 'Email inválido.' });
        } else if (err.code === 'auth/missing-email') {
          setError({ message: 'Ingresa tu email.' });
        } else if (err.code === 'auth/user-not-found') {
          setError({ message: 'Usuario no encontrado.' });
        } else if (err instanceof FieldsAPIError) setFieldErrors(err.fieldErrors);
        else setError(err);
      });
  };

  const getFieldError = (field: string) => fieldErrors && fieldErrors[field];

  return (
    <>
      {!!success ? (
        <>
          <Box className={classes.box}>
            <CheckCircleIcon className={classes.icon} fontSize='medium' />
            <Typography variant='h6'>¡Enviado!</Typography>
          </Box>
          <Box className={classes.box}>
            <Typography align='center'>
              {isWelcome
                ? 'Te enviamos un email para crear tu contraseña.'
                : 'Accede a tu email para cambiar tu contraseña.'}
            </Typography>
          </Box>
          <Button className={classes.submit} variant='contained' color='primary' fullWidth onClick={() => navigate(getLoginRoute(email))}>
            Ir a iniciar sesión
          </Button>
        </>
      ) : (
        <>
          <Typography variant='h5' gutterBottom>
            {isWelcome ? 'Creá tu contraseña' : 'Recupera tu cuenta'}
          </Typography>
          <Typography variant='subtitle2' align='center'>
            {isWelcome
              ? 'Ingresá tu email para que te enviemos un link para crear tu contraseña.'
              : 'Ingresa tu email para que te enviemos un link de cambio de contraseña.'}
          </Typography>
          <form className={classes.form} noValidate>
            <TextField
              type='text'
              name='email'
              label='Email'
              margin='normal'
              onChange={handleChange}
              value={email}
              required
              fullWidth
              error={getFieldError('email') !== undefined}
              helperText={getFieldError('email')}
            />
            <Button className={classes.submit} type='submit' variant='contained' color='primary' fullWidth onClick={submit}>
              Enviar
            </Button>
          </form>
        </>
      )}
      <Link className={classes.link} to={getLoginRoute(email)}>
        <Typography variant='subtitle2'>Volver</Typography>
      </Link>
    </>
  );
}
